import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, useTheme } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sun, Moon, Menu, X, User, LogOut, LayoutDashboard, Settings, Bell, FileText, FolderKanban, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const { user, token, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch user notifications (not for admin)
  useEffect(() => {
    if (user && user.role !== "admin" && token) {
      fetchNotifications();
    }
  }, [user, token]);

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API}/api/user/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch (e) {
      console.error("Failed to fetch notifications");
    }
  };

  const markNotificationsRead = async () => {
    try {
      await fetch(`${API}/api/user/notifications/mark-read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications([]);
    } catch (e) {
      console.error("Failed to mark notifications");
    }
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Filtrer les liens selon le rôle
  const allNavLinks = [
    { name: t("nav.blog"), path: "/", showForTeam: true },
    { name: t("nav.portfolio"), path: "/portfolio", showForTeam: true },
    { name: t("nav.projects"), path: "/projects", showForTeam: true },
    { name: "Équipe", path: "/equipe", showForTeam: true },
    { name: t("nav.about"), path: "/a-propos", showForTeam: true },
    { name: t("nav.contact"), path: "/contact", showForTeam: false },
  ];
  
  const isTeamMember = user?.role === "admin" || user?.role === "editor";
  const navLinks = isTeamMember 
    ? allNavLinks.filter(link => link.showForTeam)
    : allNavLinks;

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-background/95 backdrop-blur-xl shadow-lg border-b border-border"
            : "bg-background/80 backdrop-blur-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo with User Profile - Clickable for menu */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 group" data-testid="user-profile-trigger">
                      {user.profile_image ? (
                        <img 
                          src={user.profile_image.startsWith('/api') ? `${API}${user.profile_image}` : user.profile_image}
                          alt={user.full_name}
                          className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover cursor-pointer"
                        />
                      ) : (
                        <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-muted flex items-center justify-center cursor-pointer">
                          <User className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <span className="hidden sm:block font-heading font-bold text-base md:text-lg text-foreground group-hover:text-primary transition-colors truncate">
                        Michel Berger
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    <div className="px-3 py-2 border-b">
                      <p className="font-medium text-sm truncate">{user.full_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                    {user.role === "admin" && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" data-testid="admin-link" className="flex items-center gap-2">
                          <LayoutDashboard className="h-4 w-4" />
                          {t("nav.adminDashboard")}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/settings" data-testid="settings-link" className="flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        {t("settings.title")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={logout}
                      data-testid="logout-btn"
                      className="flex items-center gap-2 text-red-500"
                    >
                      <LogOut className="h-4 w-4" />
                      {t("nav.logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/" className="flex items-center gap-2 group">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-muted flex items-center justify-center">
                    <User className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <span className="hidden sm:block font-heading font-bold text-base md:text-lg text-foreground group-hover:text-primary transition-colors truncate">
                    Michel Berger
                  </span>
                </Link>
              )}
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-5 xl:gap-7">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`nav-link text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive(link.path)
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-1 md:gap-2">
              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
                className="rounded-full h-9 w-9 md:h-10 md:w-10"
              >
                {theme === "dark" ? (
                  <Sun className="h-4 w-4 md:h-5 md:w-5" />
                ) : (
                  <Moon className="h-4 w-4 md:h-5 md:w-5" />
                )}
              </Button>

              {/* Notifications for regular users */}
              {user && user.role !== "admin" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid="notifications-trigger"
                      className="rounded-full h-9 w-9 md:h-10 md:w-10 relative"
                    >
                      <Bell className="h-4 w-4 md:h-5 md:w-5" />
                      {notifications.length > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                          {notifications.length}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <div className="px-3 py-2 border-b flex justify-between items-center">
                      <p className="font-medium text-sm">Notifications</p>
                      {notifications.length > 0 && (
                        <button 
                          onClick={markNotificationsRead}
                          className="text-xs text-primary hover:underline"
                        >
                          Tout effacer
                        </button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                        Aucune notification
                      </div>
                    ) : (
                      notifications.slice(0, 5).map((notif) => (
                        <DropdownMenuItem key={notif.id} asChild>
                          <Link to={notif.link} className="flex items-center gap-3 py-2">
                            {notif.type === "article" && <FileText className="h-4 w-4 text-primary" />}
                            {notif.type === "project" && <FolderKanban className="h-4 w-4 text-primary" />}
                            {notif.type === "suggestion" && <Sparkles className="h-4 w-4 text-primary" />}
                            <span className="text-sm truncate">{notif.title}</span>
                          </Link>
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Login button for non-authenticated users */}
              {!user && (
                <Link to="/login">
                  <Button
                    data-testid="login-btn"
                    size="sm"
                    className="hidden sm:flex rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-xs md:text-sm px-4 md:px-6"
                  >
                    {t("nav.login")}
                  </Button>
                </Link>
              )}

              {/* Mobile Menu Toggle */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden rounded-full h-9 w-9"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            />
            
            {/* Menu Panel */}
            <motion.div
              initial={{ opacity: 0, x: "100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 w-[280px] z-50 bg-background border-l border-border shadow-xl lg:hidden"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <span className="font-heading font-bold text-lg">Menu</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="rounded-full h-9 w-9"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>

                {/* Navigation Links */}
                <div className="flex-1 overflow-y-auto py-4">
                  <div className="flex flex-col gap-1 px-2">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`flex items-center px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                          isActive(link.path)
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-border">
                  {user ? (
                    <div className="space-y-3">
                      <div className="px-2">
                        <p className="font-medium text-sm">{user.full_name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                      {user.role === "admin" && (
                        <Link to="/admin" className="block">
                          <Button variant="outline" className="w-full justify-start gap-2">
                            <LayoutDashboard className="h-4 w-4" />
                            {t("nav.adminDashboard")}
                          </Button>
                        </Link>
                      )}
                      <Button
                        variant="destructive"
                        onClick={logout}
                        className="w-full"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        {t("nav.logout")}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Link to="/login">
                        <Button className="w-full" data-testid="mobile-login-btn">
                          {t("nav.login")}
                        </Button>
                      </Link>
                      <Link to="/register">
                        <Button variant="outline" className="w-full">
                          {t("auth.createAccount")}
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
